import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithGoogle } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const needsRole = searchParams.get('needs_role') === 'true';
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError('Authentication was cancelled or failed.');
      toast.error('Google authentication failed');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    if (!token) {
      setError('No authentication token received. Please try again.');
      toast.error('Authentication failed.');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    const handleCallback = async () => {
      try {
        const user = await loginWithGoogle(token);
        if (needsRole || user.user_type === 'pending') {
          navigate('/select-role');
        } else {
          toast.success(`Welcome${user.name ? ', ' + user.name : ''}!`);
          const isAdminUser = user.user_type === 'admin' || user.user_type === 'super_admin' || user.user_type === 'sub_admin' || user.user_type === 'school_admin';
          navigate(isAdminUser ? '/admin' : '/dashboard');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError('Authentication failed. Please try again.');
        toast.error('Authentication failed. Please try again.');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [loginWithGoogle, navigate, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <span className="text-2xl text-red-500 font-bold">!</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-emerald-600" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Completing Sign In...</h2>
        <p className="text-gray-600">Please wait while we authenticate your account.</p>
      </div>
    </div>
  );
}
